import { action } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didInsert, willDestroy } from '@ember/render-modifiers';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Select } from 'ember-power-select/components/power-select';
import type { PowerSelectTriggerSignature } from 'ember-power-select/components/power-select/trigger';

import type {
  Group,
  Option,
} from '../../../../types/components/prism/combo-select-input.ts';
import PrismBadge from '../../badge.gts';
import PrismIcon from '../../icon.gts';

interface PrismComboSelectInputUnsafeNavigationTriggerArgs {
  isActive: boolean;
  isSmall: boolean;
}

interface PrismComboSelectInputUnsafeNavigationTriggerSignature {
  Args: PowerSelectTriggerSignature<Option, Group<Option>>['Args'] &
    PrismComboSelectInputUnsafeNavigationTriggerArgs;
}

/**
 * A special trigger only used in the main navigation that opens the dropdown on hover
 * manual event listeners had to be set in order to make this pattern work. It's far from ideal
 * and not accessible. The trigger also doesn't include a focus state because of this.
 */
export default class PrismComboSelectInputUnsafeNavigationTrigger extends Component<PrismComboSelectInputUnsafeNavigationTriggerSignature> {
  @tracked mouseDownHandler?: (event: MouseEvent) => void;

  @tracked mouseEnterHandler?: (event: MouseEvent) => void;

  @tracked mouseLeaveHandler?: (event: MouseEvent) => void;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get labelClass() {
    if (this.args.isActive) {
      if (this.args.isSmall) {
        return 'text-300--bold';
      }

      return 'text-400--bold';
    }

    if (this.args.isSmall) {
      return 'text-300--medium';
    }

    return 'text-400--regular';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get select() {
    return this.args.select as Select<Option, Group<Option>>;
  }

  @action
  removeOpenOnHoverHandlers(element: HTMLElement): void {
    const triggerContainer = element.parentNode as HTMLElement | null;
    if (!triggerContainer) {
      return;
    }

    if (this.mouseDownHandler) {
      triggerContainer.removeEventListener('mousedown', this.mouseDownHandler);
    }

    const selectContainer = triggerContainer.parentNode as HTMLElement;

    if (this.mouseEnterHandler) {
      selectContainer?.removeEventListener(
        'mouseenter',
        this.mouseEnterHandler,
      );
    }
    if (this.mouseLeaveHandler) {
      selectContainer?.removeEventListener(
        'mouseleave',
        this.mouseLeaveHandler,
      );
    }
  }

  @action
  setOpenOnHoverHandlers(element: HTMLElement): void {
    // Prevent select from closing when clicking on trigger even though the dropdown is open because of it's being hovered
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const mouseDownHandler = (event: MouseEvent) => {
      event.stopImmediatePropagation();
    };

    this.mouseDownHandler = mouseDownHandler;

    const triggerContainer = element.parentNode as HTMLElement;
    triggerContainer.addEventListener('mousedown', this.mouseDownHandler);

    // Set manual event listeners to open and close the select on hover
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const mouseEnterHandler = () => {
      this.args.select.actions.open();
    };
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const mouseLeaveHandler = () => {
      // Close the select and skip focus
      this.args.select.actions.close(undefined, true);
    };

    this.mouseEnterHandler = mouseEnterHandler;
    this.mouseLeaveHandler = mouseLeaveHandler;

    const selectContainer = triggerContainer.parentNode as HTMLElement;
    selectContainer.addEventListener('mouseenter', this.mouseEnterHandler);
    selectContainer.addEventListener('mouseleave', this.mouseLeaveHandler);
  }

  <template>
    <p
      class="trigger__label {{this.labelClass}}"
      {{didInsert this.setOpenOnHoverHandlers}}
      {{willDestroy this.removeOpenOnHoverHandlers}}
    >
      {{this.select.selected.label}}
    </p>
    {{#if this.select.selected.badge}}
      <PrismBadge
        @badgeType={{if
          this.select.selected.badgeType
          this.select.selected.badgeType
          "info"
        }}
        class="ml-200"
      >
        {{this.select.selected.badge}}
      </PrismBadge>
    {{/if}}
    {{#if @select.isOpen}}
      <PrismIcon @name="chevron-up" @size="small" />
    {{else}}
      <PrismIcon @name="chevron-down" @size="small" />
    {{/if}}
  </template>
}
